import { DownloadSimple, Play } from "@phosphor-icons/react";

export const money = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const csvCell = (v) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const downloadCsv = (name, rows) => {
  if (!rows?.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => csvCell(r[k])).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

export const Stat = ({ label, value, tone = "text-white", testId }) => (
  <div data-testid={testId} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
    <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
    <div className={`font-mono-data mt-1 text-xl font-bold ${tone}`}>{value}</div>
  </div>
);

// Header shared by every report: title, optional date range, Generate + CSV.
export const ReportShell = ({
  id,
  title,
  blurb,
  range,
  from,
  to,
  onFrom,
  onTo,
  onGenerate,
  busy,
  csvRows,
  csvName,
  generated,
  children,
}) => (
  <section data-testid={`report-${id}`} className="animate-in fade-in duration-300">
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
        <p className="mt-1 max-w-xl text-sm text-zinc-500">{blurb}</p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        {range && (
          <>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">
              From
              <input
                type="date"
                value={from}
                onChange={(e) => onFrom(e.target.value)}
                data-testid={`report-${id}-from`}
                className="mt-1 block h-10 rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100"
              />
            </label>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">
              To
              <input
                type="date"
                value={to}
                onChange={(e) => onTo(e.target.value)}
                data-testid={`report-${id}-to`}
                className="mt-1 block h-10 rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100"
              />
            </label>
          </>
        )}
        <button
          type="button"
          onClick={onGenerate}
          disabled={busy}
          data-testid={`report-${id}-generate`}
          className="flex h-10 items-center gap-2 rounded-full bg-yellow-500 px-5 text-xs font-bold uppercase tracking-widest text-black transition-colors hover:bg-yellow-400 disabled:opacity-50"
        >
          <Play size={14} weight="fill" /> {busy ? "Generating…" : generated ? "Refresh" : "Generate"}
        </button>
        <button
          type="button"
          onClick={() => downloadCsv(csvName, csvRows)}
          disabled={!csvRows?.length}
          data-testid={`report-${id}-csv`}
          className="flex h-10 items-center gap-2 rounded-full border border-zinc-700 px-4 text-xs font-bold uppercase tracking-widest text-zinc-200 transition-colors hover:border-zinc-500 disabled:opacity-40"
        >
          <DownloadSimple size={14} weight="bold" /> CSV
        </button>
      </div>
    </div>
    {!generated && !busy ? (
      <div
        data-testid={`report-${id}-empty`}
        className="mt-8 rounded-lg border border-dashed border-zinc-800 p-10 text-center text-sm text-zinc-500"
      >
        Press Generate to build this report{range ? " for the chosen dates" : ""}.
      </div>
    ) : (
      <div className="mt-6">{children}</div>
    )}
  </section>
);

// Simple responsive table: columns [{key, label, right, render}]
export const Table = ({ columns, rows, rowKey, testId, empty = "Nothing to show." }) => (
  <div className="overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-900" data-testid={testId}>
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500">
          {columns.map((c) => (
            <th key={c.key} className={`px-4 py-3 font-semibold ${c.right ? "text-right" : "text-left"}`}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr>
            <td colSpan={columns.length} className="p-8 text-center text-zinc-500">
              {empty}
            </td>
          </tr>
        )}
        {rows.map((r) => (
          <tr key={rowKey(r)} className="border-b border-zinc-800/60 last:border-0 hover:bg-zinc-800/40">
            {columns.map((c) => (
              <td
                key={c.key}
                className={`px-4 py-2.5 ${c.right ? "font-mono-data text-right" : ""} ${c.className || ""}`}
              >
                {c.render ? c.render(r) : r[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
